import { Key, useRef } from 'react'
import { useListState } from '@react-stately/list'
import { useListBox } from '@react-aria/listbox'
import { Item } from '@react-stately/collections'
import Styles from './PreviewAccountSelect.module.scss'

import { ExternalIntegration } from 'api/clients/ExternalIntegrationsClient'
import { PreviewAccountOption } from './PreviewAccountOption/PreviewAccountOption'
import useEmblaCarousel from 'embla-carousel-react'
import { DEFAULT_OPTIONS } from 'components/Carousel/Carousel'

interface BasePreviewAccountSelectProps {
  externalIntegrations: ExternalIntegration[]
  selectedAccount: ExternalIntegration | null
  onAccountSelect: (i: ExternalIntegration) => void
  image?: string
  variant?: 'default' | 'avatar'
}

export const PreviewAccountSelect = ({
  externalIntegrations,
  selectedAccount,
  onAccountSelect,
  image,
  variant = 'default',
}: BasePreviewAccountSelectProps) => {
  const state = useListState({
    items: externalIntegrations,
    children: (integration) => (
      <Item key={integration.id.toString()} textValue={integration.name}>
        {integration.name}
      </Item>
    ),
    selectionMode: 'single',
    selectedKeys: selectedAccount
      ? new Set<Key>([selectedAccount.id.toString()])
      : new Set([externalIntegrations[0]?.id.toString()]),
    onSelectionChange: (keys) => {
      const idStr = [...keys][0] as string | undefined
      if (!idStr) return
      const id = Number(idStr)
      const found = externalIntegrations.find((i) => i.id === id)
      if (found) onAccountSelect(found)
    },
  })

  const embla = useEmblaCarousel({ ...DEFAULT_OPTIONS, dragFree: true })

  const ref = useRef<HTMLUListElement>(null)
  const { listBoxProps } = useListBox({ 'aria-label': 'Select social account' }, state, ref)

  return (
    <div className={Styles.carouselContainer}>
      <div className={Styles.carouselWrapper} ref={embla[0]}>
        <ul {...listBoxProps} ref={ref} className={Styles.root} data-testid="preview-account-listbox">
          {[...state.collection].map((node) => (
            <PreviewAccountOption
              key={node.key}
              node={node}
              state={state}
              image={image || node.value?.picture}
              variant={variant}
            />
          ))}
        </ul>
      </div>
    </div>
  )
}
